import useUrlState from '@ahooksjs/use-url-state'
import { InfoCircleOutlined, LeftOutlined, LockOutlined, PlusCircleOutlined, RightOutlined } from '@ant-design/icons'
import { useRequest } from 'ahooks'
import { Avatar, Button, DatePicker, Drawer, Form, Input, Popconfirm, Popover, Table, Tooltip, Typography } from 'antd'
import { ColumnType } from 'antd/es/table'
import { produce } from 'immer'
import { useAtom } from 'jotai'
import { isEqual, isNil, isNull, omit, omitBy, partition, pick, uniq } from 'lodash'
import moment from 'moment'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { v4 } from 'uuid'

import { getPointPermission, UsersSearch } from '@/components'
import ss from '@/components/work-resource/click-away/click-away.module.less'
import { ClickAwayDepartmentSearch } from '@/components/work-resource/click-away/click-away-department-search.component'
// import { ClickAwayInputNumber } from '@/components/resource-table/click-away/click-away-input-number.component'
// import { ClickAwayRangePicker } from '@/components/resource-table/click-away/click-away-range-picker.component'
import { ClickAwayUserSearch } from '@/components/work-resource/click-away/click-away-user-search.component'
import { ClickAwayWeekInputNumber } from '@/components/work-resource/click-away/click-away-week-input-number.component'
import { ProjectWorkResourceInput } from '@/components/work-resource/special-project/special-project-work-resource.container'
import s from '@/components/work-resource/special-project/special-project-work-resource.module.less'
import {
  getCreatingRecordFromTree,
  getIsValidWeekToUpsert,
  getParentByKey,
  getRecordByKey,
  ProjectTableColumnKey,
  setCreatingRecordToSubTree,
} from '@/components/work-resource/special-project/special-project-work-resource.util'
import { TdCell } from '@/components/work-resource/special-project/td-cell.component'
import { userInfoAtom, usersAtom } from '@/modules/user'
import {
  PAGE_RESOURCE_POINTS_ENUM,
  Project,
  ProjectStatus,
  SpecificProjectWorkResource,
  WorkResourceRowType,
} from '@/schemas'

import { getIsColumnRequired, validateFields } from './project-work-resource.util'

export const ProjectWorkResourceContainer: React.FC<{
  project: Project
  isClosingProject?: boolean
  onOKUpdate?: () => void
  onLoading?: (value: boolean) => void
}> = React.forwardRef(({ project, onOKUpdate, onLoading, isClosingProject }, ref) => {
  const { t } = useTranslation()

  const [users] = useAtom(usersAtom)
  const [viewer] = useAtom(userInfoAtom)
  const location = useLocation()
  const urlParams = new URLSearchParams(location.search)
  const urlYear = urlParams.get('year')
  const urlWeek = urlParams.get('week')
  const isProjectInSpecialProject = (project.workResourceRelations || [])?.length > 0
  const isSuperAdmin = viewer.isSuperAdmin

  const { canCreate, canDelete, canUpdate } = getPointPermission(
    PAGE_RESOURCE_POINTS_ENUM.资源管理,
    project.permissions
  )

  const [openDrawer, setOpenDrawer] = React.useState<{ record: ProjectWorkResourceInput; readonly: boolean }>()
  const [drawerContent, setDrawerContent] = React.useState('')
  const [records, setRecords] = React.useState<ProjectWorkResourceInput[]>([])
  const [expandKeys, setExpandKeys] = React.useState<string[]>([])
  const initMomentDate = moment().isoWeekday() < 3 ? moment().subtract(1, 'week') : moment()
  const [state, setState] = useUrlState({
    year: urlYear || initMomentDate.isoWeekYear(),
    week: urlWeek || initMomentDate.isoWeek(),
  })

  const [weekDate, setWeekDate] = React.useState<{
    year: number
    week: number
    startDate: moment.Moment
    endDate: moment.Moment
  }>({
    year: initMomentDate.isoWeekYear(),
    week: initMomentDate.isoWeek(),
    startDate: initMomentDate.startOf('isoWeek'),
    endDate: initMomentDate.endOf('isoWeek'),
  })

  const {
    data,
    refresh,
    loading: getLoading,
  } = useRequest<SpecificProjectWorkResource[]>(
    () => ({
      url: `/api/work-resource/getProjectWorkResource?${new URLSearchParams({
        projectSelfId: project.selfId,
        year: weekDate.year.toString(),
        week: weekDate.week.toString(),
        startDate: weekDate.startDate.format('YYYY-MM-DD'),
        endDate: weekDate.endDate.format('YYYY-MM-DD'),
      })}`,
      method: 'get',
    }),
    { refreshDeps: [weekDate] }
  )

  const { run, loading: creatingLoading } = useRequest(
    (data) => ({
      url: `/api/work-resource/createProjectUserWorkResource`,
      data: {
        ...data,
        projectSelfId: project.selfId,
      },
      method: 'post',
    }),
    {
      manual: true,
      onSuccess() {
        refresh()
      },
    }
  )

  const { run: deleteRun, loading: deleteLoading } = useRequest(
    (data) => ({
      url: `/api/work-resource/deleteWorkResource?${new URLSearchParams(data)}`,
      method: 'delete',
    }),
    {
      manual: true,
      onSuccess() {
        refresh()
      },
    }
  )

  const { run: updateRun, loading: updateLoading } = useRequest(
    (data) => ({
      url: '/api/work-resource/updateWorkResource',
      method: 'patch',
      data,
    }),
    {
      manual: true,
      onSuccess() {
        refresh()
      },
    }
  )

  const { run: commentRun, loading: commentLoading } = useRequest(
    (data) => ({
      url: '/api/work-resource/upsertWeekWorkResourceComment',
      method: 'post',
      data,
    }),
    {
      manual: true,
      onSuccess() {
        refresh()
      },
    }
  )

  const { run: isProjectValidRun, loading: validLoading } = useRequest(
    {
      url: `/api/work-resource/getIsProjectWorkResourcesValid?${new URLSearchParams({
        projectSelfId: project.selfId,
        validateType: isClosingProject ? 'closing' : 'other',
      })}`,
    },
    {
      manual: true,
      onSuccess() {
        // after hook
        if (onOKUpdate) {
          onOKUpdate()
        }
      },
    }
  )

  React.useImperativeHandle(ref, () => {
    return {
      onSubmit: () => {
        // 后端校验 work resource
        isProjectValidRun()
      },
    }
  })

  const isProjectFinished = project.status === ProjectStatus.已结项

  React.useEffect(() => {
    // 防止 invalid year, week, 如year=ata10&year=invalid33
    const year = !urlYear || Number.isNaN(Number(urlYear)) ? initMomentDate.isoWeekYear() : Number(urlYear)
    const week = !urlWeek || Number.isNaN(Number(urlWeek)) ? initMomentDate.isoWeek() : Number(urlWeek)
    const date = initMomentDate.clone().isoWeekYear(year).isoWeek(week).startOf('isoWeek')
    // 防止越界 week, 如 week=60
    setState({ year: date.isoWeekYear(), week: date.isoWeek() })
  }, [])

  React.useEffect(() => {
    onLoading?.(getLoading || creatingLoading || deleteLoading || updateLoading || validLoading)
  }, [getLoading, creatingLoading, deleteLoading, updateLoading, validLoading])

  React.useEffect(() => {
    const year = state.year
    const week = state.week
    const startDate = initMomentDate.clone().isoWeekYear(year).isoWeek(week).startOf('isoWeek')
    const endDate = initMomentDate.clone().isoWeekYear(year).isoWeek(week).endOf('isoWeek')
    setWeekDate({ year, week, startDate, endDate })
  }, [state])

  React.useEffect(() => {
    if (data) {
      setRecords((prev) => {
        const creatingData: ProjectWorkResourceInput[] = []
        getCreatingRecordFromTree(prev, creatingData)
        const [rootCreatingData, subTreeCreatingData] = partition(creatingData, (d) => isNull(d.parentKey))

        const newState = produce(data, (draft) => {
          setCreatingRecordToSubTree(draft, subTreeCreatingData)
        })
        return [...(newState as ProjectWorkResourceInput[]), ...rootCreatingData]
      })
    }
  }, [data])

  React.useEffect(() => {
    const week = openDrawer?.record?.weeks?.[0]
    setDrawerContent(week?.comment?.comment || '')
  }, [openDrawer])

  const columns: ColumnType<ProjectWorkResourceInput>[] = [
    {
      title: (
        <Form.Item
          style={{ marginBottom: 0 }}
          label={t('work.resource.table.title.department')}
          required={getIsColumnRequired(ProjectTableColumnKey.Department, project.status)}
          colon={false}
        />
      ),
      dataIndex: ProjectTableColumnKey.Department,
      key: ProjectTableColumnKey.Department,
      width: 240,
      fixed: 'left',
      render: (_, record) => {
        const isProjectRow = record.type === WorkResourceRowType.Project
        if (isProjectRow) {
          const names = uniq((record.children || []).map((department) => department.departmentName))
          return (
            <Tooltip title={names.join(', ')}>
              <div className='ml-8.5 w-[140px]'>
                {names.slice(0, 2).map((name) => (
                  <div key={name} className='whitespace-nowrap overflow-hidden overflow-ellipsis'>
                    {name}
                  </div>
                ))}
                {names.length > 2 && <div key='others'>...</div>}
              </div>
            </Tooltip>
          )
        }

        const isDepartmentRow = record.type === WorkResourceRowType.Department
        const isActive = record.isCreating
        const readonly = record.isArchived || !canUpdate || !isProjectInSpecialProject
        const includeEmails = record.departmentId
          ? users.filter((u) => u.departmentIds.includes(record.departmentId))
          : undefined
        return (
          <div className='flex gap-2'>
            <ClickAwayDepartmentSearch
              iconClassName={isActive || isDepartmentRow ? '' : ss.displayDepartment}
              wrapperClassName={isDepartmentRow ? '' : ss.departmentVisibility}
              textClassName={'whitespace-nowrap overflow-hidden overflow-ellipsis max-w-32'}
              required={getIsColumnRequired(ProjectTableColumnKey.Department, project.status)}
              readonly={readonly}
              isActive={isActive}
              searchComponentStyle={{ maxWidth: 160 }}
              name={record.departmentName}
              id={record.departmentId}
              onChange={(value, label) => {
                setRecords((prev) =>
                  produce(prev, (draft) => {
                    const currentRecords: ProjectWorkResourceInput[] = []
                    getRecordByKey(record.key, draft, currentRecords)
                    const [currentRecord] = currentRecords

                    if (currentRecord) {
                      currentRecord.departmentId = value
                      currentRecord.departmentName = label?.[0] || ''
                    }
                  })
                )
                // 记录存在直接更新
                if (record.userWorkResourceSelfId) {
                  updateRun({
                    projectSelfId: project.selfId,
                    userWorkResourceSelfId: record.userWorkResourceSelfId,
                    departmentId: value,
                    departmentName: label?.[0] || '',
                  })
                }
              }}
            />
            {isDepartmentRow && !record.isCreating && !record.isArchived && isProjectInSpecialProject && (
              <span>
                <Popover
                  trigger='click'
                  title={
                    <Typography.Text className='font-medium text-[16px] text-[#191919] leading-[24px]'>
                      {t('add users')}
                    </Typography.Text>
                  }
                  content={
                    <div>
                      <UsersSearch
                        onChange={(v) => {
                          if (v) {
                            setRecords((prev) => {
                              const newState = produce(prev, (draft) => {
                                // 获取当前节点
                                const records: ProjectWorkResourceInput[] = []
                                getRecordByKey(record.key, draft, records)
                                const [draftRecord] = records

                                const newItem = {
                                  key: `${draftRecord.key}-${v4()}`,
                                  type: WorkResourceRowType.User,
                                  subprojectWorkResourceSelfId: null,
                                  userWorkResourceSelfId: '',
                                  subprojectName: '',
                                  subprojectSelfId: project!.selfId || '',
                                  departmentId: draftRecord.departmentId,
                                  departmentName: draftRecord.departmentName,
                                  email: v,
                                  startDate: (project.startDate
                                    ? moment(project.startDate).format('YYYY-MM-DD')
                                    : undefined) as string | null,
                                  endDate: (project.endDate
                                    ? moment(project.endDate).format('YYYY-MM-DD')
                                    : undefined) as string | null,
                                  predictWorkload: null,
                                  actualWorkload: null,
                                  project,
                                  quarters: [],
                                  weeks: [],
                                  isCreating: true,
                                  parentKey: draftRecord.key,
                                }
                                draftRecord['children'] = draftRecord?.children
                                  ? [newItem, ...draftRecord.children]
                                  : [newItem]
                              })

                              return newState
                            })
                            setExpandKeys((prev) => uniq([...prev, record.key]))
                          }
                        }}
                        includeEmails={includeEmails}
                        placeholder={t('search users')}
                        style={{ width: 200 }}
                      />
                    </div>
                  }
                >
                  <PlusCircleOutlined />
                </Popover>
              </span>
            )}
          </div>
        )
      },
      shouldCellUpdate: (curr, prev) =>
        !isEqual(curr.children, prev.children) ||
        curr.departmentId !== prev.departmentId ||
        curr.departmentName !== prev.departmentName ||
        curr.email !== prev.departmentName ||
        curr.isCreating !== prev.isCreating,
    },
    {
      title: (
        <Form.Item
          style={{ marginBottom: 0 }}
          label={t('work.resource.table.title.people')}
          required={getIsColumnRequired(ProjectTableColumnKey.Email, project.status)}
          colon={false}
        />
      ),
      dataIndex: ProjectTableColumnKey.Email,
      key: ProjectTableColumnKey.Email,
      width: 160,
      render: (value, record) => {
        const isCreating = record.isCreating

        if ([WorkResourceRowType.Project, WorkResourceRowType.Custom].includes(record.type) && !isCreating) {
          const emails = (record.children || []).flatMap((child) => child.children || []).map((child) => child.email)
          const filteredUsers = users.filter((u) => emails.includes(u.email))
          return (
            <Avatar.Group
              size='small'
              maxCount={5}
              maxStyle={{ color: '#f56a00', backgroundColor: '#fde3cf', cursor: 'pointer' }}
            >
              {filteredUsers.map((u) => {
                return (
                  <Tooltip key={u.email} title={u.name}>
                    <Avatar src={u.avatar} className='cursor-pointer' />
                  </Tooltip>
                )
              })}
            </Avatar.Group>
          )
        }

        if (record.type === WorkResourceRowType.Department && !isCreating) {
          const emails = (record.children || []).map((r) => r.email)
          const filteredUsers = users.filter((u) => emails.includes(u.email))
          return (
            <Avatar.Group
              size='small'
              maxCount={5}
              maxStyle={{ color: '#f56a00', backgroundColor: '#fde3cf', cursor: 'pointer' }}
            >
              {filteredUsers.map((u) => {
                return (
                  <Tooltip key={u.email} title={u.name}>
                    <Avatar src={u.avatar} className='cursor-pointer' />
                  </Tooltip>
                )
              })}
            </Avatar.Group>
          )
        }

        if (record.type !== WorkResourceRowType.User && !isCreating) {
          return null
        }

        const isUserNotEmployee =
          record.type === WorkResourceRowType.User && users.every((u) => u.email !== record.email) && !record.isCreating
        const readonly = record.isArchived || !canUpdate || isUserNotEmployee || !isProjectInSpecialProject
        const includeEmails = record.departmentId
          ? users.filter((u) => u.departmentIds.includes(record.departmentId))
          : undefined
        return (
          <ClickAwayUserSearch
            includeEmails={includeEmails}
            required={getIsColumnRequired(ProjectTableColumnKey.Email, project.status)}
            isActive={isCreating}
            record={record}
            value={value}
            readonly={readonly}
            onChange={(v) => {
              setRecords((prev) =>
                produce(prev, (draft) => {
                  const user = users.find((u) => u.email === v)
                  const currentRecords: ProjectWorkResourceInput[] = []
                  getRecordByKey(record.key, draft, currentRecords)
                  const [currentRecord] = currentRecords

                  if (currentRecord) {
                    currentRecord.email = v
                    const department = user?.departments[0]
                    if (department) {
                      currentRecord.departmentId = currentRecord.departmentId || department?.id || 0
                      currentRecord.departmentName = currentRecord.departmentName || department?.name || ''
                    } else {
                      currentRecord.departmentId = 0
                      currentRecord.departmentName = ''
                    }
                  }
                })
              )
              if (record.userWorkResourceSelfId) {
                updateRun({
                  projectSelfId: project.selfId,
                  userWorkResourceSelfId: record.userWorkResourceSelfId,
                  email: v,
                })
              }
            }}
          />
        )
      },
      shouldCellUpdate: (curr, prev) =>
        !isEqual(curr.children, prev.children) ||
        curr.email !== prev.email ||
        curr.departmentId !== prev.departmentId ||
        curr.isCreating !== prev.isCreating,
    },
    // {
    //   title: (
    //     <Form.Item
    //       style={{ marginBottom: 0 }}
    //       label={t('work.resource.table.title.predictWorkload')}
    //       required={getIsColumnRequired(ProjectTableColumnKey.PredictWorkload, project.status)}
    //       colon={false}
    //     />
    //   ),
    //   dataIndex: ProjectTableColumnKey.PredictWorkload,
    //   key: ProjectTableColumnKey.PredictWorkload,
    //   width: 120,
    //   render: (value, record) => {
    //     if (record.type === WorkResourceRowType.Portfolio) {
    //       return null
    //     }

    //     const isUserNotEmployee =
    //       record.type === WorkResourceRowType.User && users.every((u) => u.email !== record.email) && !record.isCreating
    //     const readonly =
    //       record.isArchived ||
    //       (!record.isCreating && record.type !== WorkResourceRowType.User) ||
    //       !canUpdate ||
    //       isUserNotEmployee
    //     return (
    //       <ClickAwayInputNumber
    //         key='predict'
    //         required={getIsColumnRequired(ProjectTableColumnKey.PredictWorkload, project.status)}
    //         readonly={readonly}
    //         isActive={record.isCreating}
    //         value={value}
    //         onUpdate={(v) => {
    //           updateRun({
    //             projectSelfId: project.selfId,
    //             userWorkResourceSelfId: record.userWorkResourceSelfId,
    //             predictWorkload: Number(v),
    //           })
    //         }}
    //         onChange={(v) => {
    //           setRecords((prev) =>
    //             produce(prev, (draft) => {
    //               const currentRecords: ProjectWorkResourceInput[] = []
    //               getRecordByKey(record.key, draft, currentRecords)
    //               const [currentRecord] = currentRecords

    //               if (currentRecord) {
    //                 currentRecord.predictWorkload = !isNil(v) ? Number(v) : null
    //               }
    //             })
    //           )
    //         }}
    //       />
    //     )
    //   },
    //   shouldCellUpdate: (curr, prev) => {
    //     return Number(curr.predictWorkload) !== Number(prev.predictWorkload) || curr.isCreating !== prev.isCreating
    //   },
    // },
    {
      title: (
        <Form.Item style={{ marginBottom: 0 }} label={t('work.resource.table.title.actualWorkload')} colon={false} />
      ),
      dataIndex: ProjectTableColumnKey.ActualWorkload,
      key: ProjectTableColumnKey.ActualWorkload,
      width: 120,
      render: (_, record) => {
        if (record.type === WorkResourceRowType.Portfolio) {
          return null
        }

        return (
          <span className={ss.readonlyText}>
            {!isNil(record.actualWorkload) ? Number(record.actualWorkload) : '待评估'}
          </span>
        )
      },
      shouldCellUpdate: (curr, prev) =>
        Number(curr.actualWorkload) !== Number(prev.actualWorkload) || curr.isCreating !== prev.isCreating,
    },
    // {
    //   title: (
    //     <Form.Item
    //       style={{ marginBottom: 0 }}
    //       label={t('work.resource.table.title.period')}
    //       required={getIsColumnRequired(ProjectTableColumnKey.Period, project.status)}
    //       colon={false}
    //     />
    //   ),
    //   dataIndex: ProjectTableColumnKey.Period,
    //   key: ProjectTableColumnKey.Period,
    //   width: 280,
    //   shouldCellUpdate: (curr, prev) =>
    //     curr.startDate !== prev.startDate ||
    //     curr.endDate !== prev.endDate ||
    //     curr.isCreating !== prev.isCreating ||
    //     curr.email !== prev.email,
    //   render: (_, record) => {
    //     const isUserNotEmployee =
    //       record.type === WorkResourceRowType.User && users.every((u) => u.email !== record.email) && !record.isCreating
    //     const readonly =
    //       record.isArchived ||
    //       (!record.isCreating && record.type !== WorkResourceRowType.User) ||
    //       (record.isCreating && record.type === WorkResourceRowType.Department && !record.email) || // 只创建部门时使用默认周期
    //       !canUpdate ||
    //       isUserNotEmployee

    //     return (
    //       <ClickAwayRangePicker
    //         required={getIsColumnRequired(ProjectTableColumnKey.Period, project.status)}
    //         isActive={record.isCreating}
    //         readonly={readonly}
    //         startDate={record.startDate ? moment(record.startDate) : undefined}
    //         endDate={record.endDate ? moment(record.endDate) : undefined}
    //         onChange={(_, dateStrings) => {
    //           const [startDate, endDate] = dateStrings
    //           setRecords((prev) =>
    //             produce(prev, (draft) => {
    //               const currentRecords: ProjectWorkResourceInput[] = []
    //               getRecordByKey(record.key, draft, currentRecords)
    //               const [currentRecord] = currentRecords

    //               if (currentRecord) {
    //                 currentRecord.startDate = startDate
    //                 currentRecord.endDate = endDate
    //               }
    //             })
    //           )
    //           if (record.userWorkResourceSelfId && startDate && endDate) {
    //             updateRun({
    //               projectSelfId: project.selfId,
    //               userWorkResourceSelfId: record.userWorkResourceSelfId,
    //               startDate: startDate,
    //               endDate: endDate,
    //             })
    //           }
    //         }}
    //       />
    //     )
    //   },
    // },
    {
      title: (
        <Form.Item
          style={{ marginBottom: 0 }}
          label={t('work.resource.table.title.weekWorkload')}
          required={false}
          colon={false}
        />
      ),
      width: 160,
      dataIndex: ProjectTableColumnKey.WeekWorkload,
      key: ProjectTableColumnKey.WeekWorkload,
      // cell 更新受到 weekDate state 影响，这里不检查更新逻辑
      // shouldCellUpdate: (curr, prev) =>
      //   !isEqual(curr, prev) ||
      //   curr.startDate !== prev.startDate ||
      //   curr.endDate !== prev.endDate ||
      //   curr.isCreating !== prev.isCreating,
      render: (_, record) => {
        const isPortfolio = record.type === WorkResourceRowType.Portfolio
        if (isPortfolio) {
          return null
        }

        const week = record.weeks.find(
          (week) => week.week === weekDate.week.toString() && week.year === weekDate.year.toString()
        ) || {
          year: weekDate.year.toString(),
          week: weekDate.week.toString(),
          startDate: weekDate.startDate.format('YYYY-MM-DD'),
          endDate: weekDate.endDate.format('YYYY-MM-DD'),
          workload: null,
        }

        const shouldWeekBeArchived =
          !getIsValidWeekToUpsert({ year: Number(week.year), week: Number(week.week) }) || week.isArchived
        const isUserNotEmployee =
          record.type === WorkResourceRowType.User && users.every((u) => u.email !== record.email) && !record.isCreating
        const readonly =
          record.isArchived ||
          (!record.isCreating && record.type !== WorkResourceRowType.User) ||
          !canUpdate ||
          isUserNotEmployee ||
          !isProjectInSpecialProject ||
          shouldWeekBeArchived
        const existWeek = record?.weeks?.[0]
        return (
          <div className='flex gap-2 items-center'>
            <ClickAwayWeekInputNumber
              key={`${record.key}-${week.year}-${week.week}`}
              year={weekDate.year}
              week={weekDate.week}
              value={week}
              readonly={readonly}
              isActive={record.isCreating}
              showLockedIcon={shouldWeekBeArchived}
              required={false}
              onUpdate={(value) => {
                if (record.userWorkResourceSelfId) {
                  const weekToUpdate = { ...week, workload: Number(value) }
                  updateRun({
                    projectSelfId: project.selfId,
                    userWorkResourceSelfId: record.userWorkResourceSelfId,
                    week: weekToUpdate,
                  })
                }
              }}
              onChange={(value) => {
                setRecords((prev) =>
                  produce(prev, (draft) => {
                    const currentRecords: ProjectWorkResourceInput[] = []
                    getRecordByKey(record.key, draft, currentRecords)
                    const [currentRecord] = currentRecords

                    if (currentRecord) {
                      const index = currentRecord.weeks.findIndex((q) => q.week === week.week && q.year === week.year)
                      if (~index) {
                        currentRecord.weeks[index] = {
                          ...week,
                          workload: Number(value),
                        }
                      } else {
                        currentRecord.weeks = [{ ...week, workload: Number(value) }]
                      }
                    }
                  })
                )
              }}
            />
            {record.type === WorkResourceRowType.User && existWeek?.comment?.comment && (
              <Tooltip title='备注'>
                <InfoCircleOutlined
                  className='cursor-pointer'
                  onClick={() => {
                    setOpenDrawer({ record, readonly: true })
                  }}
                />
              </Tooltip>
            )}
          </div>
        )
      },
    },
    {
      title: t('work.resource.table.title.operation'),
      dataIndex: 'operation',
      key: 'operation',
      width: 120,
      fixed: 'right',
      // shouldCellUpdate: (curr, prev) => !isEqual(curr, prev) || curr.isCreating !== prev.isCreating,
      render: (_, record) => {
        const isPortfolio = record.type === WorkResourceRowType.Portfolio
        const isProject = record.type === WorkResourceRowType.Project
        const isUserNotEmployee =
          record.type === WorkResourceRowType.User && users.every((u) => u.email !== record.email) && !record.isCreating

        // 结项项目或离职员工不可编辑此条信息
        if (record.isArchived || isUserNotEmployee) {
          return <LockOutlined />
        }

        // 非用户行不可编辑
        if (isPortfolio || isProject) {
          return null
        }

        if (record.isCreating) {
          return (
            <div className='flex justify-between'>
              <Button
                type='link'
                onClick={() => {
                  const data = { ...omit(record, ['isCreating', 'key']), projectSelfId: project.selfId }
                  if (validateFields(data, getIsColumnRequired, project.status)) {
                    run(data)
                    setRecords((prev) => {
                      const newState = produce(prev, (draft) => {
                        const parents: ProjectWorkResourceInput[] = []
                        getParentByKey(record.key, draft, parents)
                        const [parent] = parents
                        // 非 root 节点
                        if (parent) {
                          parent['children'] = (parent.children || []).filter((child) => child.key !== record.key)
                        } else {
                          // root 节点
                          return draft.filter((d) => d.key !== record.key)
                        }
                      })
                      return newState
                    })
                  }
                }}
              >
                {t('save')}
              </Button>
              <Button
                type='link'
                onClick={() => {
                  setRecords((prev) => {
                    const newState = produce(prev, (draft) => {
                      const parents: ProjectWorkResourceInput[] = []
                      getParentByKey(record.key, draft, parents)
                      const [parent] = parents
                      // 非 root 节点
                      if (parent) {
                        parent['children'] = (parent.children || []).filter((child) => child.key !== record.key)
                      } else {
                        // root 节点
                        return draft.filter((d) => d.key !== record.key)
                      }
                    })
                    return newState
                  })
                }}
              >
                {t('cancel')}
              </Button>
            </div>
          )
        }

        const commentButton =
          record.type === WorkResourceRowType.User && canUpdate && isProjectInSpecialProject ? (
            <Button
              type='link'
              onClick={() => {
                setOpenDrawer({ record, readonly: false })
              }}
            >
              {t('work.resource.comment')}
            </Button>
          ) : null

        const deleteButton =
          canDelete && isProjectInSpecialProject && isSuperAdmin ? (
            <Popconfirm
              title={t(`work.resource.${record.type}.deleteConfirm`)}
              onConfirm={() => {
                const data = omitBy(pick(record, ['subprojectWorkResourceSelfId', 'userWorkResourceSelfId']), isNil)
                deleteRun({ ...data, projectSelfId: project.selfId })
              }}
            >
              <Button type='text' danger>
                {t('remove')}
              </Button>
            </Popconfirm>
          ) : null

        const week = record.weeks.find(
          (week) => week.week === weekDate.week.toString() && week.year === weekDate.year.toString()
        )
        const invisibleButton =
          record.type === WorkResourceRowType.User &&
          canUpdate &&
          isProjectInSpecialProject &&
          !record.invisibleDate &&
          !Number(week?.workload) &&
          moment().isBetween(weekDate.startDate, weekDate.endDate, 'date', '[]') ? (
            <Popconfirm
              title={
                <div className='max-w-[300px]'>
                  <div className='whitespace-pre-wrap'>{t('work.resource.invisible.confirm1')}</div>
                  <div className='whitespace-pre-wrap'>{t('work.resource.invisible.confirm2')}</div>
                </div>
              }
              onConfirm={() => {
                updateRun({
                  projectSelfId: project.selfId,
                  userWorkResourceSelfId: record.userWorkResourceSelfId,
                  invisibleDate: moment().startOf('isoWeek').format('YYYY-MM-DD'),
                })
              }}
            >
              <Button type='link'>{t('work.resource.invisible.title')}</Button>
            </Popconfirm>
          ) : null

        return (
          <div className='flex gap-2'>
            {commentButton}
            {invisibleButton}
            {deleteButton}
          </div>
        )
      },
    },
  ]

  return (
    <div>
      <div className='flex mb-4 gap-4 justify-start'>
        <DatePicker
          allowClear={false}
          picker='week'
          format={`${weekDate.startDate.format('YYYY-MM-DD')}-${weekDate.endDate.format('YYYY-MM-DD')} (GGGG-Wo)`}
          value={moment(weekDate.startDate)}
          onChange={(value) => {
            if (value) {
              const year = value.isoWeekYear()
              const week = value.isoWeek()
              setState({ year, week })
            }
          }}
        />
        <Button
          onClick={() => {
            const yearWeek = moment().isoWeekYear(state.year).isoWeek(state.week).subtract(1, 'week')
            const year = yearWeek.isoWeekYear()
            const week = yearWeek.isoWeek()
            setState({ year, week })
          }}
          icon={<LeftOutlined />}
        />
        <Button
          onClick={() => {
            const yearWeek = moment().isoWeekYear(state.year).isoWeek(state.week).add(1, 'week')
            const year = yearWeek.isoWeekYear()
            const week = yearWeek.isoWeek()
            setState({ year, week })
          }}
          icon={<RightOutlined />}
        />
      </div>
      <Table
        rowClassName={(record) => {
          if (record.type === WorkResourceRowType.Department) {
            return s.departmentRow
          }
          if ([WorkResourceRowType.Custom, WorkResourceRowType.Project].includes(record.type)) {
            return s.subprojectRow
          }
          if (record.type === WorkResourceRowType.Portfolio) {
            return s.portfolioRow
          }
          return s.row
        }}
        scroll={{ x: 'max-content' }}
        components={{
          body: { cell: TdCell },
        }}
        loading={getLoading || creatingLoading || deleteLoading || updateLoading}
        pagination={false}
        bordered
        expandable={{
          expandedRowKeys: expandKeys,
          onExpandedRowsChange: (keys) => setExpandKeys(keys as string[]),
          rowExpandable: (r) => !r.isCreating,
        }}
        columns={columns}
        dataSource={records}
      />
      {isProjectFinished || !canCreate || !isProjectInSpecialProject ? null : (
        <div className='flex my-4 justify-end'>
          <Button
            type='primary'
            onClick={() => {
              setRecords((prev) => [
                ...prev,
                {
                  key: v4(),
                  type: WorkResourceRowType.User,
                  subprojectWorkResourceSelfId: null,
                  userWorkResourceSelfId: null,
                  subprojectName: null,
                  subprojectSelfId: project.selfId,
                  departmentId: 0,
                  departmentName: '',
                  email: '',
                  startDate: (project.startDate ? moment(project.startDate).format('YYYY-MM-DD') : undefined) as any,
                  endDate: (project.endDate ? moment(project.endDate).format('YYYY-MM-DD') : undefined) as any,
                  predictWorkload: null,
                  actualWorkload: null,
                  quarters: [],
                  weeks: [],
                  isCreating: true,
                  parentKey: null,
                },
              ])
            }}
          >
            {t('add resource estimate')}
          </Button>
        </div>
      )}
      <Drawer
        title={openDrawer?.readonly ? '备注详情' : '填写备注'}
        placement='right'
        onClose={() => setOpenDrawer(undefined)}
        visible={!!openDrawer}
      >
        <Input.TextArea
          readOnly={openDrawer?.readonly}
          value={drawerContent}
          onChange={(e) => setDrawerContent(e.target.value)}
          placeholder={t('work.resource.commentPlaceholder')}
          autoSize={{ minRows: 8 }}
        />
        {!openDrawer?.readonly && canUpdate && isProjectInSpecialProject && (
          <div className='flex my-4 justify-end'>
            <Button
              loading={commentLoading}
              type='primary'
              onClick={() => {
                const week = openDrawer?.record?.weeks?.[0]

                commentRun({
                  userWorkResourceSelfId: openDrawer?.record?.userWorkResourceSelfId,
                  year: weekDate.year.toString(),
                  week: weekDate.week.toString(),
                  startDate: weekDate.startDate.format('YYYY-MM-DD'),
                  endDate: weekDate.endDate.format('YYYY-MM-DD'),
                  userWeekWorkResourceId: week?.id,
                  comment: drawerContent,
                })
                setOpenDrawer(undefined)
              }}
            >
              {t('submit')}
            </Button>
          </div>
        )}
      </Drawer>
    </div>
  )
})
